import { NavigationGuard } from 'vue-router'
import { Parse } from 'src/boot/parse'
import { flavorLockBlock, isGeneralBuild, currentFlavor, identityFlavor } from 'src/utils/flavorLock'
import { clearDeadSession } from 'src/composables/useAuth'

const guardRoutes: (NavigationGuard) = async (to, from, next) => {
  // console.log('Planner guard', Parse.User.current(), to.path)
  const user = Parse.User.current()
  const isLogged = !!user
  const isVerified = !!user?.get('emailVerified2')
  // Coworkers log in by phone — their username is the numeric dialCode+phone
  // (admins always have an email username). Phone login already verifies them
  // via OTP, so they skip the email verification step entirely.
  const username = String(user?.get('username') || '')
  const isCoworker = isLogged && (/^\d+$/.test(username) || (!!user?.get('phone') && !user?.get('email')))

  if(to.path?.includes('admin')) {
    if(!isLogged) {
      next(`/login?redirect=${to.path}`)
    } else if(flavorLockBlock(user?.get('registered_using'))) {
      // Flavor isolation on the shared general DB: login/register/coworker-OTP already
      // gate by flavor, but a LIVE session carried to another flavor's host (e.g. spa
      // session → nail.plannera.app) slipped through. Log out + bounce to login, which
      // shows the "registered for {Flavor}" banner (?flavorLocked). No-op on non-general
      // builds (table/tablePL). Only on 'admin' paths → /login isn't admin → no loop.
      const block = flavorLockBlock(user?.get('registered_using'))!
      Parse.User.logOut()
      next(`/login?flavorLocked=${block.otherFlavor ?? 'none'}`)
    } else if(!isVerified && !isCoworker) {
      next(`/verification?redirect=${to.path}`)
    } else {
      const foreign = await isForeignFlavorBusiness(to.params?.businessId as string | undefined)
      if (foreign === 'expired') {
        // The session is DEAD, not the business foreign — say so, don't blame the flavor.
        await clearDeadSession()
        next('/login?expired=1')
      } else if (foreign) {
        // Business-level flavor isolation (general DB only): the whole general catalog lives
        // in ONE Parse DB, so a business is fetchable by id regardless of flavor. Even an
        // in-lane user (spa user on spa.plannera.app) could open a NAIL business by pasting
        // its id. Block any business whose businessType != this build's flavor; bounce to the
        // business list. (Client gate only — the durable boundary is a server-side read
        // restriction by the user's registered_using flavor.)
        next('/admin?wrongFlavor=1')
      } else {
        next()
      }
    }
  } else {
    next()
  }
}

/**
 * True when `businessId` names a Business belonging to a DIFFERENT general flavor than
 * this build. Only meaningful on general builds (the shared Plannera DB); always false on
 * the DreamDiner 'planner' backend. Fetches ONLY businessType. Fails CLOSED: a missing/
 * unreadable business (e.g. ACL-denied once the server lock lands) is treated as foreign.
 */
async function isForeignFlavorBusiness(businessId: string | undefined): Promise<boolean | 'expired'> {
  if (!isGeneralBuild || !businessId) return false
  try {
    const biz = await new Parse.Query('Business').select('businessType').get(businessId)
    // Compare IDENTITIES, not keys: a CLONE flavor (wellza) and its base (spa) are one
    // product over one set of venues. Without this, the wellza app listed SpaDemo and then
    // bounced straight back to /admin when it was tapped — the business looked "foreign"
    // to a build that is literally the spa app under another name.
    return identityFlavor(biz?.get('businessType') || 'table') !== identityFlavor(currentFlavor)
  } catch (err) {
    // ⚠️ A dead SESSION is not a foreign business. Failing closed on it told the owner
    // "this business belongs to another flavor" for their own business (2026-09-29).
    if ((err as { code?: number })?.code === Parse.Error.INVALID_SESSION_TOKEN) return 'expired'
    return true
  }
}

export default guardRoutes